import dayjs from 'dayjs';
import { ReactNode } from 'react';
import { Book } from 'pages/dashboard';
import { renderFormattedDate } from 'utils';
import { Project, User } from 'library/types';
import CloseIcon from '@mui/icons-material/Close';
import { FormattedMessage, useIntl } from 'react-intl';
import { ProjectStepDetailRow } from 'pages/project-step-managing/types';
import { PROJECT_STEP_PRIORITY_TYPE_ENUM, PROJECT_STEP_STATUS_TYPE_ENUM } from 'enums';
import { Box, Chip, Stack, Drawer, Divider, Typography, IconButton, Toolbar, alpha, useTheme } from '@mui/material';

type DetailItem = { label: string; value: ReactNode };

type DetailDrawerProps = {
  users: User[];
  books: Book[];
  products: Project[];
  open: boolean;
  close: () => void;
  detailRow: ProjectStepDetailRow | null;
  getProjectStepStatusTypeTranslated: (status: number) => string;
  getProjectStepPriorityTypeTranslated: (priority: number) => string;
  getProjectStepResponsibleContactTypeTranslated: (type: number) => string;
};

const DetailDrawer = ({
  users,
  books,
  products,
  open,
  close,
  detailRow,
  getProjectStepStatusTypeTranslated,
  getProjectStepPriorityTypeTranslated,
  getProjectStepResponsibleContactTypeTranslated,
}: DetailDrawerProps) => {
  const intl = useIntl();
  const theme = useTheme();

  const renderStatusChip = (status?: number) => {
    const currentStatus = status ?? PROJECT_STEP_STATUS_TYPE_ENUM.REQUESTED;

    const colorKey =
      currentStatus === PROJECT_STEP_STATUS_TYPE_ENUM.REQUESTED
        ? 'info'
        : currentStatus === PROJECT_STEP_STATUS_TYPE_ENUM.IN_PROGRESS
          ? 'secondary'
          : 'success';

    return (
      <Chip
        size="small"
        label={getProjectStepStatusTypeTranslated(currentStatus)}
        sx={{
          fontWeight: 600,
          bgcolor: alpha(theme.palette[colorKey].main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
          color: theme.palette[colorKey].main,
          border: `1px solid ${alpha(theme.palette[colorKey].main, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`,
          cursor: 'default',
        }}
      />
    );
  };

  const renderPriorityChip = (priority?: number) => {
    const currentPriority = priority ?? PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT;

    const colorKey =
      currentPriority === PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT
        ? 'success'
        : currentPriority === PROJECT_STEP_PRIORITY_TYPE_ENUM.URGENT
          ? 'warning'
          : 'error';

    return (
      <Chip
        size="small"
        label={getProjectStepPriorityTypeTranslated(currentPriority)}
        sx={{
          fontWeight: 600,
          bgcolor: alpha(theme.palette[colorKey].main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
          color: theme.palette[colorKey].main,
          border: `1px solid ${alpha(theme.palette[colorKey].main, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`,
          cursor: 'default',
        }}
      />
    );
  };

  const renderRowDetails = (row: ProjectStepDetailRow | null): DetailItem[] => {
    if (!row) return [];

    return [
      {
        label: intl.formatMessage({ id: 'projectStepManaging.book', defaultMessage: 'Proje' }),
        value: books.find((b) => b.id === row.bookId)?.companyName ?? '-',
      },
      {
        label: intl.formatMessage({ id: 'project.table.product', defaultMessage: 'Ürün' }),
        value: products.find((p) => p.id === row.projectId)?.title ?? '-',
      },
      {
        label: intl.formatMessage({ id: 'projectStepManaging.projectStep', defaultMessage: 'Proje Adımı' }),
        value: row.title ?? '-',
      },
      {
        label: intl.formatMessage({
          id: 'projectStepManaging.stepExplanation',
          defaultMessage: 'Adım Açıklaması',
        }),
        value: row.description ?? '-',
      },
      {
        label: intl.formatMessage({ id: 'effortTracing.status', defaultMessage: 'Statü' }),
        value: renderStatusChip(row.status),
      },
      {
        label: intl.formatMessage({ id: 'projectStepManaging.priority', defaultMessage: 'Öncelik' }),
        value: renderPriorityChip(row.priority),
      },
      {
        label: intl.formatMessage({
          id: 'projectStepManaging.relatedContact',
          defaultMessage: 'İlgili Kişisi',
        }),
        value: users.find((u) => u.id === row.relatedContactId)?.nameSurname ?? '-',
      },
      {
        label: intl.formatMessage({
          id: 'projectStepManaging.alternativeRelatedContact',
          defaultMessage: 'Yedek İlgili Kişisi',
        }),
        value: users.find((u) => u.id === row.alternativeRelatedContactId)?.nameSurname ?? '-',
      },
      {
        label: intl.formatMessage({ id: 'productProjectPlan.responsibleContact', defaultMessage: 'Sorumlu' }),
        value: row.responsibleContactType
          ? getProjectStepResponsibleContactTypeTranslated(row.responsibleContactType)
          : '-',
      },
      {
        label: intl.formatMessage({
          id: 'productProjectPlan.estimatedTaskDay',
          defaultMessage: 'Tahmini Adım Süresi (gün)',
        }),
        value: row.estimatedTaskDay ?? '-',
      },
      {
        label: intl.formatMessage({
          id: 'projectStepManaging.additionalEffortDay',
          defaultMessage: 'Ek Efor Süresi (gün)',
        }),
        value: row.additionalEffortDay ?? '-',
      },
      {
        label: intl.formatMessage({ id: 'projectStepManaging.terminateDate', defaultMessage: 'Termin' }),
        value: row.dueDate ? renderFormattedDate(dayjs(row.dueDate)) : '-',
      },
      {
        label: intl.formatMessage({ id: 'effortTracing.createdAt', defaultMessage: 'Oluşturulma Tarihi' }),
        value: row.createdAt ? renderFormattedDate(dayjs(row.createdAt)) : '-',
      },
      {
        label: intl.formatMessage({
          id: 'projectStepManaging.lastUpdateDate',
          defaultMessage: 'Son Güncellenme',
        }),
        value: row.updatedAt ? renderFormattedDate(dayjs(row.updatedAt)) : '-',
      },
      {
        label: intl.formatMessage({
          id: 'projectStepManaging.notes',
          defaultMessage: 'Notlar',
        }),
        value: row.notes ?? '-',
      },
      {
        label: intl.formatMessage({ id: 'projectStepManaging.comments', defaultMessage: 'Yorumlar' }),
        value:
          row.comments.length === 0 ? (
            <Typography variant="caption" color="text.disabled">
              <FormattedMessage id="projectStepManaging.noCommentYet" defaultMessage="Henüz yorum eklenmemiş" />
            </Typography>
          ) : (
            <Stack spacing={0.75}>
              {row.comments.map((item) => {
                return (
                  <Box
                    key={item.id}
                    sx={{
                      position: 'relative',
                      pr: 0,
                      borderRadius: 1,
                      '&:hover .comment-delete-action': {
                        opacity: 0,
                        pointerEvents: 'none',
                      },
                    }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        pr: 0,
                      }}>
                      {renderFormattedDate(item.createdAt, true)} •{' '}
                      {users.find((user) => user.id === item.userId)?.nameSurname}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        lineHeight: 1.35,
                      }}>
                      {item.commentText}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          ),
      },
    ];
  };

  return (
    <Drawer anchor="right" open={open} onClose={close}>
      <Toolbar />
      <Box sx={{ width: { xs: '92vw', sm: 520 }, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6">
            <FormattedMessage id="common.detail" defaultMessage="Detay" />
          </Typography>

          <IconButton onClick={close}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>
          {[
            books.find((b) => b.id === detailRow?.bookId)?.companyName,
            products.find((p) => p.id === detailRow?.projectId)?.title,
          ]
            .filter(Boolean)
            .join(' / ')}
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Stack spacing={1.25}>
          {renderRowDetails(detailRow).map((item) => (
            <Box key={item.label}>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                {item.label}
              </Typography>
              <Typography component="div" variant="body2" sx={{ wordBreak: 'break-word' }}>
                {item.value}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    </Drawer>
  );
};

export default DetailDrawer;
